import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { DOCTOR_ROUTES } from "../../../routes/patientRoutes/routePaths";
import "./WaitingPatientCard.css";

/* ─── Icons ────────────────────────────────────────────── */

const ClockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const FileIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);

const DragIcon = () => (
  <svg viewBox="0 0 20 20" fill="currentColor" width="12" height="12">
    <circle cx="7" cy="4" r="1.5" />
    <circle cx="13" cy="4" r="1.5" />
    <circle cx="7" cy="10" r="1.5" />
    <circle cx="13" cy="10" r="1.5" />
    <circle cx="7" cy="16" r="1.5" />
    <circle cx="13" cy="16" r="1.5" />
  </svg>
);

const ArrowNextIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M5 12h14" />
    <path d="M13 6l6 6-6 6" />
  </svg>
);

const CreditCardIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
    <line x1="1" y1="10" x2="23" y2="10" />
  </svg>
);

const getInitials = (patient = {}) => {
  const firstName = String(patient.firstName || "").trim();
  const lastName = String(patient.lastName || "").trim();

  const first = firstName[0] || "";
  const last = lastName[0] || "";
  const initials = `${first}${last}`.trim();

  if (initials) return initials.toUpperCase();

  const name = String(patient.name || "").trim();
  if (!name) return "?";

  return (
    name
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part[0] || "")
      .join("")
      .toUpperCase() || "?"
  );
};

/* ─── Composant ────────────────────────────────────────── */

export default function WaitingPatientCard({
  patient,
  onRemove,
  onAdvance,
  onRequestPayment,
  inConsultation,
  onOpenPayment,
  onOpenPaymentHistory,
  onLoadPatientAppointments,
  onLoadPatientDetails,
  showOpenFile = true,
  canDrag = false,
}) {
  const navigate = useNavigate();
  const { t } = useTranslation("waitingRoom");
  const [isHovered, setIsHovered] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [nextAppointment, setNextAppointment] = useState(null);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: patient.id, disabled: !canDrag });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.35 : 1,
    zIndex: isDragging ? 999 : "auto",
    cursor: canDrag ? "grab" : "default",
  };

  const dragProps = canDrag ? { ...attributes, ...listeners } : {};

  const normalizedPaymentStatus = String(
    paymentDetails?.paymentStatus || patient.paymentStatus || ""
  ).toLowerCase();
  const isPaid = normalizedPaymentStatus === "paid";
  const isPartial = normalizedPaymentStatus === "partial";

  const isCompleted = patient.status === "completed";
  const isExpanded = Boolean(inConsultation || isHovered || isDetailsOpen);
  const initials = getInitials(patient);

  const stopPropagation = (event) => {
    event.stopPropagation();
  };

  const handleToggleCard = () => {
    if (isCompleted) {
      setIsDetailsOpen((previous) => !previous);
      if (!isDetailsOpen && !isLoadingDetails) {
        setIsLoadingDetails(true);
        const loadDetails = onLoadPatientDetails || onLoadPatientAppointments;
        Promise.resolve(loadDetails?.(patient))
          .then((details) => {
            if (Array.isArray(details)) {
              setNextAppointment(details[0] || null);
              return;
            }
            setNextAppointment(details?.nextAppointment || null);
            setPaymentDetails(details?.payment || null);
          })
          .finally(() => setIsLoadingDetails(false));
      }
      return;
    }
    if (!canDrag) return;
    if (isDragging) return;
    onRequestPayment?.(patient);
  };

  const handleViewFile = (event) => {
    event.stopPropagation();
    const targetId = patient.effectiveProfileId || patient.subprofileId || patient.patientId || patient.id;
    if (!targetId) return;
    const query = new URLSearchParams();
    if (patient.patientId) query.set("patientId", patient.patientId);
    if (patient.subprofileId) query.set("subprofileId", patient.subprofileId);
    navigate(`${DOCTOR_ROUTES.FILE_PAGE(targetId)}${query.toString() ? `?${query}` : ""}`);
  };

  const handleViewPaymentHistory = (event) => {
    event.stopPropagation();
    if (onOpenPaymentHistory) {
      onOpenPaymentHistory(patient);
      return;
    }
    const targetId = patient.effectiveProfileId || patient.subprofileId || patient.patientId || patient.id;
    if (!targetId) return;
    const query = new URLSearchParams();
    const principalPatientId = patient.patientId || patient.principalUserId || patient.id;
    if (principalPatientId) query.set("patientId", principalPatientId);
    if (patient.subprofileId) query.set("subprofileId", patient.subprofileId);
    const returnTo = `${DOCTOR_ROUTES.FILE_PAGE(targetId)}?${query}`;
    const paymentRoute = DOCTOR_ROUTES.PATIENT_PAYMENT_HISTORY(principalPatientId || targetId);
    navigate(`${paymentRoute}${patient.subprofileId ? `?subprofileId=${encodeURIComponent(patient.subprofileId)}` : ""}`, {
      state: { returnTo, patient },
    });
  };

  const handleRemove = (event) => {
    event.stopPropagation();
    onRemove && onRemove(patient.id);
  };

  const handleAdvance = (event) => {
    event.stopPropagation();
    if (!canDrag) return;
    onAdvance && onAdvance(patient.id);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...dragProps}
      className={[
        "wpc",
        isExpanded ? "wpc--open" : "",
        isDragging ? "wpc--dragging" : "",
        inConsultation ? "wpc--in-consultation" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      onClick={handleToggleCard}
      onMouseEnter={() => canDrag && !inConsultation && setIsHovered(true)}
      onMouseLeave={() => {
        if (canDrag && !inConsultation) setIsHovered(false);
      }}
      role={canDrag || isCompleted ? "button" : undefined}
      tabIndex={canDrag || isCompleted ? 0 : -1}
      onKeyDown={(event) => {
        if (!canDrag && !isCompleted) return;
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          handleToggleCard();
        }
      }}
    >
      {/* Drag handle */}
      {canDrag ? (
        <button
          ref={setActivatorNodeRef}
          type="button"
          className="wpc__drag"
          {...attributes}
          {...listeners}
          onClick={stopPropagation}
          tabIndex={-1}
          aria-label={t("card.drag")}
        >
          <DragIcon />
        </button>
      ) : null}

      {/* Remove */}
      <button
        type="button"
        className="wpc__remove"
        onClick={handleRemove}
        aria-label={t("card.remove")}
      >
        ×
      </button>

      {/* ── Header ── */}
      <div className="wpc__header">
        <div className="wpc__top-row">
          {/* Avatar */}
          <div className="wpc__avatar" aria-hidden="true">
            {patient.avatarUrl ? (
              <img
                className="wpc__avatar-img"
                src={patient.avatarUrl}
                alt={patient.name || "patient"}
                onError={(event) => {
                  event.currentTarget.style.display = "none";
                  const fallback = event.currentTarget.parentElement;
                  if (fallback) fallback.setAttribute("data-fallback", initials);
                }}
              />
            ) : (
              <span className="wpc__avatar-fallback">{initials}</span>
            )}
          </div>

          {/* Name + source/age badges */}
          <div className="wpc__identity">
            <span className="wpc__name">{patient.name}</span>
            <div className="wpc__badge-row">
              <span
                className={`wpc__badge wpc__badge--source wpc__source--${String(
                  patient.source || ""
                ).toLowerCase()}`}
              >
                {patient.source}
              </span>
              <span className="wpc__badge wpc__badge--age">
                {patient.age ?? "--"} {t("card.years")}
              </span>
            </div>
          </div>

          {/* PTN identifier — déplacé à droite, hors des badges */}
          {patient.identifier ? (
            <span className="wpc__identifier">{patient.identifier}</span>
          ) : null}
        </div>

        {/* Time + reason sur une seule ligne */}
        <div className="wpc__meta-inline">
          <span className="wpc__meta-time">
            <ClockIcon />
            {patient.time || "--:--"}
          </span>
          <span className="wpc__meta-reason">
            <FileIcon />
            {patient.reason || t("card.noReason", "Consultation")}
          </span>
        </div>
      </div>

      {/* ── Zone détails (expandable) ── */}
      <div className="wpc__expandable">
        {/* Footer: payment + advance */}
        <div className="wpc__footer">
          <button
            type="button"
            className="wpc__history-btn"
            onClick={handleViewPaymentHistory}
            title={t("card.paymentHistory")}
          >
            <CreditCardIcon />
            {t("card.paymentHistory")}
          </button>
          {isCompleted ? (
            <div
              className={`wpc__payment wpc__payment--readonly ${
                isPaid
                  ? "wpc__payment--paid"
                  : isPartial
                  ? "wpc__payment--partial"
                  : "wpc__payment--unpaid"
              }`}
            >
              <CreditCardIcon />
              {isPaid || isPartial
                ? `${t("card.paid")} ${paymentDetails?.amount || patient.amount || ""}`
                : t("card.payment")}
            </div>
          ) : (
            <button
              type="button"
              className={`wpc__payment ${
                isPaid
                  ? "wpc__payment--paid"
                  : isPartial
                  ? "wpc__payment--partial"
                  : "wpc__payment--unpaid"
              }`}
              onClick={(e) => {
                e.stopPropagation();
                onOpenPayment && onOpenPayment(patient);
              }}
            >
              <CreditCardIcon />
              {isPaid || isPartial
                ? `${t("card.paid")} ${paymentDetails?.amount || patient.amount || ""}`
                : t("card.payment")}
            </button>
          )}

          {!isCompleted && canDrag && (
            <button
              type="button"
              className="wpc__advance-btn"
              onClick={handleAdvance}
              title={t("card.moveNext")}
              aria-label={t("card.moveNext")}
              disabled={!canDrag}
            >
              <ArrowNextIcon />
            </button>
          )}
        </div>

        {isCompleted ? (
          <div className="wpc__completed-details">
            <span>
              {t("card.nextAppointment", "Next appointment")}: {isLoadingDetails
                ? t("card.loading", "Loading...")
                : nextAppointment?.displayDate
                ? nextAppointment.displayDate
                : nextAppointment?.startTime
                ? new Date(nextAppointment.startTime).toLocaleString()
                : t("card.none", "None")}
            </span>
          </div>
        ) : null}

        {/* Consultation / waiting badge + open file */}
        {inConsultation ? (
          <>
            <div className="wpc__consultation-badge">
              <span className="wpc__consultation-pulse" />
              {t("card.inConsultation")}
            </div>
            {showOpenFile ? (
              <button
                type="button"
                className="wpc__open-file-btn"
                onClick={handleViewFile}
              >
                <FileIcon />
                {t("card.openFile")}
              </button>
            ) : null}
          </>
        ) : patient.status === "waiting" && patient.waitingTime ? (
          <>
            <div className="wpc__waiting-badge">
              <span>⏱ {t("card.waiting")} {patient.waitingTime}</span>
            </div>
            {showOpenFile ? (
              <button
                type="button"
                className="wpc__open-file-btn wpc__open-file-btn--ghost"
                onClick={handleViewFile}
              >
                <FileIcon />
                {t("card.openFile")}
              </button>
            ) : null}
          </>
        ) : (
          showOpenFile ? (
            <button
              type="button"
              className="wpc__open-file-btn wpc__open-file-btn--ghost"
              onClick={handleViewFile}
            >
              <FileIcon />
              {t("card.openFile")}
            </button>
          ) : null
        )}
      </div>
    </div>
  );
}